import { flatten, has } from 'underscore';
import { VideoTemplateStateContent } from '../../types';

export function getActiveLayers(state: VideoTemplateStateContent): string[] {
  const activeLayers = flatten(
    Object.keys(state)
      .map((key): string[] | undefined => {
        const currentConfig = state[key];

        // Some configurations (like soundwave) have the 'layerId'
        // prop at the root object.
        if (has(currentConfig, 'layerId')) {
          return currentConfig.layerId;
        }

        // Some configurations (like textOverlays, slideshow etc) have
        // the 'layerId' prop inside of the data object.
        if (has(currentConfig, 'data')) {
          const { data } = currentConfig;

          return Object.keys(data).map(dataKey => data[dataKey]?.layerId);
        }

        return undefined;
      })
      .filter(Boolean),
  );

  // The active layers list is not correctly sorted at this point. This should
  // filter the original layers order list and only keep the active values so that we
  // can get the active layers in the correct order.
  return state?.layers?.order.filter(id => activeLayers.indexOf(id) > -1);
}

/**
 * It takes a state object and an assetId and returns the layerId of the asset.
 *
 * @param {VideoTemplateStateContent} state - VideoTemplateStateContent
 * @param {string} assetId - The assetId of the asset you want to get the layerId for.
 *
 * @returns The layerId of the asset.
 */
export function getLayerIdByAssetId(
  state: VideoTemplateStateContent,
  assetId: string,
): string {
  const layerIds = Object.keys(state)
    .map(key => {
      const element = state[key];

      // For cases where the layer id is in the root
      // of the object, e.g. soundwave.
      if (has(element, 'layerId') && assetId === key) {
        return element.layerId;
      }

      // For cases where the layer id is in the data
      // object and each key in that object represents an
      // asset id, e.g. textoverlays, videoclips etc.
      if (has(element, 'data') && element.data[assetId]) {
        return element.data[assetId].layerId;
      }

      return undefined;
    }, [])
    .filter(Boolean);

  return layerIds.length ? layerIds[0] : undefined;
}
